import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

const ERAS = [
  { label: 'Pre-Shot Clock',    range: '1946–1954', pace: 107.0, style: 'Methodical, low-scoring, zone defense era' },
  { label: 'Russell Era',       range: '1955–1969', pace: 118.0, style: 'Fast-break dominant, Celtics dynasty' },
  { label: 'Post-Merger',       range: '1970–1979', pace: 105.0, style: 'Physical, grinding, ABA integration' },
  { label: 'Showtime Era',      range: '1980–1989', pace: 100.0, style: 'Fast-break revolution, Magic vs Bird' },
  { label: 'Jordan Era',        range: '1990–1998', pace: 91.0,  style: 'Physical defense, hand-check rules, mid-range mastery' },
  { label: 'Post-Jordan',       range: '1999–2009', pace: 90.5,  style: 'Isolation ball, hand-check fading, zone defense allowed' },
  { label: 'Analytics Era',     range: '2010–2019', pace: 96.0,  style: '3PT revolution, pace-and-space, small-ball lineups' },
  { label: 'Modern Era',        range: '2020–present', pace: 100.0, style: 'Load management, positionless basketball, shooting premium' },
];

const PLAYERS = [
  { name: 'Wilt Chamberlain',    year: 1962, ppg: 50.4, rpg: 25.7, apg: 2.4, pos: 'C',  color: '#E74C3C' },
  { name: 'Oscar Robertson',     year: 1962, ppg: 30.8, rpg: 12.5, apg: 11.4, pos: 'PG', color: '#9B59B6' },
  { name: 'Kareem Abdul-Jabbar', year: 1972, ppg: 34.8, rpg: 16.6, apg: 4.6, pos: 'C',  color: '#2ECC71' },
  { name: 'Magic Johnson',       year: 1987, ppg: 23.9, rpg: 6.3,  apg: 12.2, pos: 'PG', color: '#C9A84C' },
  { name: 'Michael Jordan',      year: 1992, ppg: 30.1, rpg: 6.4,  apg: 6.1, pos: 'SG', color: '#F5820A' },
  { name: 'Shaquille O\'Neal',   year: 2000, ppg: 29.7, rpg: 13.6, apg: 3.8, pos: 'C',  color: '#3498DB' },
  { name: 'LeBron James',        year: 2013, ppg: 26.8, rpg: 8.0,  apg: 7.3, pos: 'SF', color: '#7B8FD4' },
  { name: 'Stephen Curry',       year: 2016, ppg: 30.1, rpg: 5.4,  apg: 6.7, pos: 'PG', color: '#F1C40F' },
];

const MODERN_PACE = 100.0;

function getEraPace(year: number): number {
  if (year < 1955) return 107.0;
  if (year < 1970) return 118.0;
  if (year < 1980) return 105.0;
  if (year < 1990) return 100.0;
  if (year < 1999) return 91.0;
  if (year < 2010) return 90.5;
  if (year < 2020) return 96.0;
  return 100.0;
}

function getEraLabel(year: number): string {
  const era = ERAS.find(e => {
    const start = parseInt(e.range.split('–')[0]);
    const end   = e.range.includes('present') ? 2025 : parseInt(e.range.split('–')[1]);
    return year >= start && year <= end;
  });
  return era?.label || 'Unknown Era';
}

interface NormalizedPlayer {
  name:        string;
  year:        number;
  pos:         string;
  color:       string;
  raw_ppg:     number;
  raw_rpg:     number;
  raw_apg:     number;
  adj_ppg:     number;
  adj_rpg:     number;
  adj_apg:     number;
  era_factor:  number;
  era_pace:    number;
  era_label:   string;
}

const EraComparison: React.FC = () => {
  const [selectedPlayers, setSelected] = useState<string[]>(['Michael Jordan', 'LeBron James']);
  const [analysis, setAnalysis]        = useState('');
  const [loading, setLoading]          = useState(false);
  const [activeTab, setActiveTab]      = useState<'table' | 'bars'>('table');

  const togglePlayer = (name: string) => {
    setSelected(prev =>
      prev.includes(name)
        ? prev.filter(p => p !== name)
        : prev.length < 4 ? [...prev, name] : prev
    );
  };

  const normalized: NormalizedPlayer[] = PLAYERS
    .filter(p => selectedPlayers.includes(p.name))
    .map(p => {
      const pace   = getEraPace(p.year);
      const factor = MODERN_PACE / pace;
      return {
        name:       p.name,
        year:       p.year,
        pos:        p.pos,
        color:      p.color,
        raw_ppg:    p.ppg,
        raw_rpg:    p.rpg,
        raw_apg:    p.apg,
        adj_ppg:    Math.round(p.ppg * factor * 10) / 10,
        adj_rpg:    Math.round(p.rpg * factor * 10) / 10,
        adj_apg:    Math.round(p.apg * factor * 10) / 10,
        era_factor: Math.round(factor * 1000) / 1000,
        era_pace:   pace,
        era_label:  getEraLabel(p.year),
      };
    });

  const runAnalysis = async () => {
    if (selectedPlayers.length < 2) return;
    setLoading(true);
    setAnalysis('');
    try {
      const playerSummaries = normalized.map(p =>
        `${p.name} (${p.era_label}): Raw PPG ${p.raw_ppg} → Era-adjusted ${p.adj_ppg} PPG (factor: ${p.era_factor})`
      ).join('; ');

      const res  = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: `Era Normalization Analysis: ${playerSummaries}. Compare these players after pace normalization to modern NBA standards. Who benefits most from era adjustment, and who would be most dominant in today's game?`
        })
      });
      const data = await res.json();
      setAnalysis(data.response || data.error);
    } catch {
      setAnalysis('Connection error. Check your API endpoint.');
    } finally {
      setLoading(false);
    }
  };

  const maxAdj = (key: 'adj_ppg' | 'adj_rpg' | 'adj_apg') =>
    Math.max(...normalized.map(p => p[key]), 1);

  return (
    <div style={{
      background: '#0D0F14', minHeight: 'calc(100vh - 48px)',
      color: '#F0EDE6', padding: '20px 24px', overflowY: 'auto'
    }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 22, color: '#F5820A', margin: 0 }}>
            Era Comparison
          </h1>
          <p style={{ fontFamily: 'sans-serif', fontSize: 12, color: '#8892A4', margin: '4px 0 0' }}>
            Pace-normalized stats across NBA eras 1946–present. Select up to 4 players.
          </p>
        </div>
        <button onClick={runAnalysis} disabled={loading || selectedPlayers.length < 2} style={{
          background: loading ? '#333' : '#F5820A',
          border: 'none', borderRadius: 8, padding: '10px 20px',
          color: '#fff', fontFamily: 'Courier New', fontSize: 11,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          cursor: loading || selectedPlayers.length < 2 ? 'not-allowed' : 'pointer'
        }}>
          {loading ? 'Analyzing...' : '◉ Run Era Analysis'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20 }}>

        {/* LEFT — Player selector */}
        <div>
          <div style={{
            background: '#13161D', border: '0.5px solid rgba(255,255,255,0.08)',
            borderRadius: 10, padding: 14
          }}>
            <div style={{
              fontFamily: 'Courier New', fontSize: 9, color: '#8892A4',
              letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12
            }}>Select Players (max 4)</div>

            {PLAYERS.map(p => {
              const isSel   = selectedPlayers.includes(p.name);
              const pace    = getEraPace(p.year);
              const factor  = MODERN_PACE / pace;
              return (
                <div key={p.name} onClick={() => togglePlayer(p.name)} style={{
                  padding: '8px 10px', borderRadius: 8, marginBottom: 6,
                  background: isSel ? `${p.color}15` : 'transparent',
                  border: `0.5px solid ${isSel ? p.color + '60' : 'rgba(255,255,255,0.06)'}`,
                  cursor: 'pointer', transition: 'all 0.15s'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{
                      fontFamily: 'sans-serif', fontSize: 12,
                      color: isSel ? '#F0EDE6' : '#8892A4', fontWeight: isSel ? 600 : 400
                    }}>{p.name}</span>
                    <span style={{
                      fontFamily: 'Courier New', fontSize: 9,
                      color: isSel ? p.color : '#8892A4',
                      background: isSel ? `${p.color}20` : 'transparent',
                      padding: '1px 5px', borderRadius: 4
                    }}>{p.pos}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                    <span style={{ fontFamily: 'Courier New', fontSize: 9, color: '#8892A4' }}>
                      {getEraLabel(p.year)}
                    </span>
                    <span style={{
                      fontFamily: 'Courier New', fontSize: 9,
                      color: factor < 1 ? '#E74C3C' : factor > 1 ? '#2ECC71' : '#8892A4'
                    }}>
                      {factor < 1 ? '▼' : factor > 1 ? '▲' : '='}{Math.abs(Math.round((factor - 1) * 100))}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Era reference */}
          <div style={{
            background: '#13161D', border: '0.5px solid rgba(255,255,255,0.08)',
            borderRadius: 10, padding: 14, marginTop: 14
          }}>
            <div style={{
              fontFamily: 'Courier New', fontSize: 9, color: '#8892A4',
              letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10
            }}>Era Pace Reference</div>
            {ERAS.map(e => (
              <div key={e.label} style={{
                display: 'flex', justifyContent: 'space-between',
                padding: '4px 0',
                borderBottom: '0.5px solid rgba(255,255,255,0.04)'
              }}>
                <span style={{ fontFamily: 'sans-serif', fontSize: 10, color: '#8892A4' }}>{e.range}</span>
                <span style={{ fontFamily: 'Courier New', fontSize: 10, color: '#F0EDE6' }}>{e.pace}</span>
              </div>
            ))}
            <div style={{ marginTop: 8, fontFamily: 'sans-serif', fontSize: 10, color: '#8892A4', lineHeight: 1.5 }}>
              Modern baseline: <span style={{ color: '#F5820A' }}>100.0</span> possessions/48 min
            </div>
          </div>
        </div>

        {/* RIGHT — Comparison output */}
        <div>

          {/* View toggle */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {(['table', 'bars'] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} style={{
                fontFamily: 'Courier New', fontSize: 9, padding: '5px 14px',
                borderRadius: 20, border: `0.5px solid ${activeTab === tab ? '#F5820A' : 'rgba(255,255,255,0.15)'}`,
                background: activeTab === tab ? 'rgba(245,130,10,0.12)' : 'transparent',
                color: activeTab === tab ? '#F5820A' : '#8892A4',
                cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase'
              }}>{tab === 'table' ? 'Stats Table' : 'Bar View'}</button>
            ))}
          </div>

          {normalized.length === 0 ? (
            <div style={{
              background: '#13161D', border: '0.5px solid rgba(255,255,255,0.06)',
              borderRadius: 10, padding: 40, textAlign: 'center'
            }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>📊</div>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: 15, color: '#8892A4' }}>
                Select players to compare
              </div>
              <div style={{ fontFamily: 'sans-serif', fontSize: 12, color: '#8892A4', marginTop: 6 }}>
                Stats are normalized to modern pace (100.0 possessions/48 min)
              </div>
            </div>
          ) : activeTab === 'table' ? (

            /* TABLE VIEW */
            <div style={{
              background: '#13161D', border: '0.5px solid rgba(255,255,255,0.08)',
              borderRadius: 10, overflow: 'hidden'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#1C2030' }}>
                    {['Player', 'Era', 'Pace', 'Factor', 'Raw PPG', 'Adj PPG', 'Raw RPG', 'Adj RPG', 'Raw APG', 'Adj APG'].map(h => (
                      <th key={h} style={{
                        fontFamily: 'Courier New', fontSize: 9, color: '#8892A4',
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                        padding: '10px 12px', textAlign: 'left',
                        borderBottom: '0.5px solid rgba(255,255,255,0.08)'
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {normalized.map((p, i) => (
                    <tr key={p.name} style={{
                      background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                      borderBottom: '0.5px solid rgba(255,255,255,0.05)'
                    }}>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 3, height: 20, background: p.color, borderRadius: 2 }} />
                          <span style={{ fontFamily: 'sans-serif', fontSize: 12 }}>{p.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', fontFamily: 'Courier New', fontSize: 10, color: '#8892A4' }}>
                        {p.era_label}
                      </td>
                      <td style={{ padding: '10px 12px', fontFamily: 'Courier New', fontSize: 11 }}>{p.era_pace}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          fontFamily: 'Courier New', fontSize: 11,
                          color: p.era_factor < 1 ? '#E74C3C' : p.era_factor > 1 ? '#2ECC71' : '#8892A4'
                        }}>×{p.era_factor}</span>
                      </td>
                      {[p.raw_ppg, p.adj_ppg, p.raw_rpg, p.adj_rpg, p.raw_apg, p.adj_apg].map((val, vi) => (
                        <td key={vi} style={{
                          padding: '10px 12px',
                          fontFamily: 'Courier New', fontSize: 12,
                          color: vi % 2 === 1 ? p.color : '#F0EDE6',
                          fontWeight: vi % 2 === 1 ? 700 : 400
                        }}>{val}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ padding: '10px 12px', borderTop: '0.5px solid rgba(255,255,255,0.06)' }}>
                <span style={{ fontFamily: 'sans-serif', fontSize: 10, color: '#8892A4' }}>
                  Adj = pace-normalized to modern NBA (100.0 possessions/48 min). Factor = 100 ÷ era pace.
                </span>
              </div>
            </div>

          ) : (

            /* BAR VIEW */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {(['adj_ppg', 'adj_rpg', 'adj_apg'] as const).map(metric => {
                const labels: Record<string, string> = { adj_ppg: 'Points Per Game (era-adj)', adj_rpg: 'Rebounds Per Game (era-adj)', adj_apg: 'Assists Per Game (era-adj)' };
                const max = maxAdj(metric);
                return (
                  <div key={metric} style={{
                    background: '#13161D', border: '0.5px solid rgba(255,255,255,0.08)',
                    borderRadius: 10, padding: 16
                  }}>
                    <div style={{
                      fontFamily: 'Courier New', fontSize: 9, color: '#8892A4',
                      letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14
                    }}>{labels[metric]}</div>
                    {normalized.map(p => (
                      <div key={p.name} style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                          <span style={{ fontFamily: 'sans-serif', fontSize: 12 }}>{p.name}</span>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <span style={{ fontFamily: 'Courier New', fontSize: 10, color: '#8892A4' }}>
                              raw: {p[metric.replace('adj_', 'raw_') as 'raw_ppg' | 'raw_rpg' | 'raw_apg']}
                            </span>
                            <span style={{ fontFamily: 'Courier New', fontSize: 13, color: p.color, fontWeight: 700 }}>
                              {p[metric]}
                            </span>
                          </div>
                        </div>
                        <div style={{ position: 'relative', height: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 4 }}>
                          {/* Raw bar */}
                          <div style={{
                            position: 'absolute', top: 2, height: 4,
                            width: `${(p[metric.replace('adj_', 'raw_') as 'raw_ppg' | 'raw_rpg' | 'raw_apg'] / max) * 100}%`,
                            background: 'rgba(255,255,255,0.15)', borderRadius: 2
                          }} />
                          {/* Adjusted bar */}
                          <div style={{
                            position: 'absolute', top: 0, height: 8,
                            width: `${(p[metric] / max) * 100}%`,
                            background: p.color, borderRadius: 4, opacity: 0.85,
                            transition: 'width 0.5s ease'
                          }} />
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* AI Analysis */}
          {(analysis || loading) && (
            <div style={{
              background: '#13161D', border: '0.5px solid rgba(245,130,10,0.3)',
              borderRadius: 10, padding: 18, marginTop: 16
            }}>
              <div style={{
                fontFamily: 'Courier New', fontSize: 9, color: '#F5820A',
                letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10
              }}>◉ Era Analysis — Bedrock</div>
              {loading
                ? <div style={{ fontFamily: 'sans-serif', fontSize: 13, color: '#8892A4' }}>
                    Normalizing across eras...
                  </div>
                : <div style={{ fontFamily: 'sans-serif', fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    {analysis}
                  </div>
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EraComparison;